import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import {
  createCustomerReview,
  lookupCustomerReviewOrder,
  uploadCustomerReviewImage,
} from '../../actions/customerReviewAction';
import { ButtonLoading } from '../../components/Button';
import privateView from '../../components/hocs/privateView';
import Analytics from '../../services/Analytics';
import { errorMessage, hasError } from '../../utils/form';
import OrderCard from './OrderCard';
import PhotoUploadCard from './PhotoUploadCard';
import ReviewFields from './ReviewFields';
import useCustomerReviewCreate from './useCustomerReviewCreate';
import styles from './CustomerReviewCreate.module.css';

const CustomerReviewCreate = props => {
  const alertRef = useRef(null);
  const pageViewRecordedRef = useRef(false);
  const form = useCustomerReviewCreate(props);
  const { injectedProps, location, match } = props;

  useEffect(() => {
    if (pageViewRecordedRef.current) return;

    pageViewRecordedRef.current = true;
    Analytics.recordPageView('customer_review_create', injectedProps.email, {
      url: window.location.href,
      location,
      match,
    });
  }, [injectedProps.email, location, match]);

  useEffect(() => {
    if (form.alert && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [form.alert]);

  const formDisabled = !form.order || form.isSubmitting;
  const createDisabled = (
    formDisabled
    || form.isUploadingPhotos
    || !form.values.displayName.trim()
    || !form.values.rating
  );
  const lookupMessage = form.lookupState === 'checking'
    ? 'Checking order…'
    : form.lookupState === 'notFound'
      ? 'No order found for this invoice number.'
      : form.lookupState === 'error'
        ? 'Could not check this invoice, please try again.'
        : 'Enter the order this review is for, then the rest of the form unlocks.';
  const lookupHasError = form.lookupState === 'notFound' || form.lookupState === 'error';
  const invoiceHasSubmitError = Boolean(form.alert && form.alert.invoiceInvalid);

  return (
    <Row className="animated fadeIn">
      <Col xs={12} md={6}>
        <Card>
          <CardHeader>SEED CUSTOMER REVIEW</CardHeader>
          <CardBody>
            <Form onSubmit={form.handleSubmit}>
              {form.alert && (
                <div className={styles.alertSection} ref={alertRef}>
                  <Alert
                    className={styles.formAlert}
                    color={form.alert.color}
                    isOpen
                    toggle={form.alert.color === 'danger' ? form.clearAlert : undefined}
                  >
                    {form.alert.message}
                  </Alert>
                  {form.alert.description && (
                    <p className={styles.alertDescription}>{form.alert.description}</p>
                  )}
                </div>
              )}

              <FormGroup className={styles.fieldGroup}>
                <div className={styles.labelRow}>
                  <Label for="customer-review-invoice" className="mb-0">Invoice Number</Label>
                  <span className={styles.required}>Required</span>
                </div>
                <Input
                  autoComplete="off"
                  disabled={form.isSubmitting}
                  id="customer-review-invoice"
                  invalid={(
                    invoiceHasSubmitError
                    || hasError('invoice_number', form.validationError)
                  )}
                  name="invoice_number"
                  onBlur={form.triggerImmediateLookup}
                  onChange={form.handleInvoiceChange}
                  onKeyDown={form.handleInvoiceKeyDown}
                  placeholder="Enter invoice number"
                  value={form.values.invoiceNumber}
                />
                {errorMessage('invoice_number', form.validationError)}
                <span
                  aria-live="polite"
                  className={lookupHasError ? styles.feedback : styles.helpText}
                  role="status"
                >
                  {lookupMessage}
                </span>
              </FormGroup>

              {form.order && <OrderCard order={form.order} />}

              <ReviewFields
                disabled={formDisabled}
                displayNameError={form.displayNameError}
                onDisplayNameBlur={form.handleDisplayNameBlur}
                onDisplayNameChange={form.handleDisplayNameChange}
                onRatingChange={form.handleRatingChange}
                onReviewTextChange={form.handleReviewTextChange}
                validationError={form.validationError}
                values={form.values}
              />

              <div className={styles.actions}>
                <Button
                  color="secondary"
                  disabled={!form.order || form.isSubmitting}
                  onClick={form.resetForm}
                  type="button"
                >
                  Cancel
                </Button>
                <ButtonLoading
                  color="primary"
                  disabled={createDisabled}
                  isLoading={form.isSubmitting}
                  loadingMessage="Creating Review…"
                  type="submit"
                >
                  Create Review
                </ButtonLoading>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <PhotoUploadCard
          alert={form.photoAlert}
          disabled={!form.order || form.isSubmitting}
          isSubmitting={form.isSubmitting}
          onFilesSelected={form.addPhotos}
          onRemove={form.removePhoto}
          onRetry={form.retryPhoto}
          photos={form.photos}
        />
      </Col>
    </Row>
  );
};

CustomerReviewCreate.propTypes = {
  createReview: PropTypes.func.isRequired,
  injectedProps: PropTypes.shape({ email: PropTypes.string }).isRequired,
  location: PropTypes.object.isRequired,
  lookupOrder: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  uploadImage: PropTypes.func.isRequired,
};

const mapStateToProps = ({ auth: { isFetch, roles, isLogged, id, email } }) => ({
  guardData: { isFetch, roles, isLogged, id, email },
});

const mapDispatchToProps = {
  createReview: createCustomerReview,
  lookupOrder: lookupCustomerReviewOrder,
  uploadImage: uploadCustomerReviewImage,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(privateView(CustomerReviewCreate));
